import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLang } from "@/components/LanguageContext";
import SEO from "@/components/SEO";
import ScrollReveal from "@/components/ScrollReveal";
import PageHero from "@/components/PageHero";
import AlliancesBlock from "@/components/AlliancesBlock";
import WhyUsBlock from "@/components/WhyUsBlock";
import CaseStudyCards from "@/components/CaseStudyCards";
import PossibilityTabs from "@/components/PossibilityTabs";
import ArticleVideo from "@/components/ArticleVideo";
import EN from "@/data/content-en";
import ZH from "@/data/content-zh";
import { PAGE_IMAGES, pathFor, NAV_GROUPS, getPageById } from "@/data/pages";
import { PAGE_ALT_IMAGES, STOCK_IMAGE_POOL } from "@/data/page-alt-images";

export default function ServiceDetailPage({ pageId }) {
  const { lang, t } = useLang();
  const all = lang === "zh" ? ZH : EN;
  const c = all[pageId];
  if (!c) return null;
  const img = PAGE_IMAGES[pageId];
  const altImg = PAGE_ALT_IMAGES[pageId] || img;
  const page = getPageById(pageId);

  // Related — same group
  const groupId = page?.group;
  const related = groupId && NAV_GROUPS[groupId] ? NAV_GROUPS[groupId].filter((id) => id !== pageId).slice(0, 3) : [];

  // Section split: 0 → AlliancesBlock, 1 → WhyUsBlock, 2+ → PossibilityTabs
  const intro = c.sections?.[0];
  const whyUs = c.sections?.[1];
  const tabsSections = (c.sections || []).slice(2);

  const whyPoints = (() => {
    if (!whyUs) return [];
    if (Array.isArray(whyUs.bullets) && whyUs.bullets.length) return whyUs.bullets.slice(0, 5);
    if (Array.isArray(whyUs.list) && whyUs.list.length) return whyUs.list.slice(0, 5).map((it) => `${it.heading} — ${it.body}`);
    if (Array.isArray(whyUs.body)) return whyUs.body;
    if (typeof whyUs.body === "string") return [whyUs.body];
    return [];
  })();

  const introParas = (() => {
    if (!intro) return [];
    const raw = Array.isArray(intro.body)
      ? intro.body
      : typeof intro.body === "string"
      ? [intro.body]
      : [];
    // Never duplicate the hero subtitle (c.intro) inside the AlliancesBlock —
    // that's bad for SEO and reads as filler. If sections[0] has no distinct
    // body copy, we let its bullets/list carry the section instead.
    const heroIntro = (c.intro || "").trim();
    return raw
      .map((p) => (typeof p === "string" ? p : ""))
      .filter((p) => p && p.trim() !== heroIntro)
      .slice(0, 3);
  })();

  // Fallback content for AlliancesBlock when sections[0] has no distinct body:
  // surface its bullets/list so the section is still informative without
  // repeating the hero subtitle.
  const introBullets =
    introParas.length === 0 && intro && Array.isArray(intro.bullets)
      ? intro.bullets
      : undefined;
  const introList =
    introParas.length === 0 && intro && Array.isArray(intro.list)
      ? intro.list
      : undefined;

  // ---- Image deduplication ---------------------------------------------
  // Build a single ordered pool of candidate images, then assign unique ones
  // to AlliancesBlock, WhyUsBlock and each PossibilityTabs tab.
  //   - Never reuse the hero image (PageHero).
  //   - Never reuse related-page images in the upper sections — those are
  //     already displayed by the CaseStudyCards row at the bottom.
  const reserved = new Set([img, ...related.map((id) => PAGE_IMAGES[id]).filter(Boolean)]);
  const used = new Set(reserved);
  const orderedPool = [altImg, ...STOCK_IMAGE_POOL];
  const nextImage = () => {
    for (const candidate of orderedPool) {
      if (candidate && !used.has(candidate)) {
        used.add(candidate);
        return candidate;
      }
    }
    // Pool exhausted (extremely long pages) — restart but still skip hero & related
    used.clear();
    reserved.forEach((r) => used.add(r));
    for (const candidate of orderedPool) {
      if (candidate && !used.has(candidate)) {
        used.add(candidate);
        return candidate;
      }
    }
    return orderedPool[0];
  };

  const alliancesImage = nextImage();
  const whyUsImage = nextImage();

  // Build tabs from remaining sections
  const tabs = tabsSections.map((sec) => ({
    heading: sec.heading,
    body: typeof sec.body === "string" ? sec.body : Array.isArray(sec.body) ? sec.body.join(" ") : undefined,
    points: sec.bullets,
    list: sec.list,
    image: nextImage(),
  }));

  const possibilityFallbackImage = nextImage();

  // Quote — pick our static brand quote
  const quote = lang === "zh"
    ? "我们的合伙人将专业技术与人际信任结合，为您的家庭、企业与下一代创造真正的成果。"
    : "Our partners bring together the right expertise and the right relationships to deliver real results for your business, your family and your future.";

  return (
    <div className={lang === "zh" ? "lang-zh" : ""}>
      <SEO pageId={pageId} title={c.title} description={c.metaDescription} image={img} />

      <PageHero pageId={pageId} mainTitle={c.h1} subtitle={c.intro} image={img} />

      {/* Alliances-style intro */}
      {intro && (
        <AlliancesBlock
          title={intro.heading || c.h1}
          paragraphs={introParas}
          bullets={introBullets}
          list={introList}
          image={alliancesImage}
          quote={quote}
          quoteAuthor={lang === "zh" ? "Graham Yuan 袁先生" : "Graham Yuan"}
          quoteRole={lang === "zh" ? "董事总经理" : "Managing Director"}
          quoteCompany="ABS Solutions"
        />
      )}

      {/* Optional inline YouTube video (blog posts) */}
      {c.youtubeId && (
        <ArticleVideo
          youtubeId={c.youtubeId}
          kicker={lang === "zh" ? "视频讲解" : "Video explainer"}
          title={c.videoTitle || (lang === "zh" ? "视频版讲解" : "Watch the video breakdown")}
          caption={c.videoCaption}
        />
      )}

      {/* Why us */}
      {whyUs && whyPoints.length > 0 && (
        <WhyUsBlock
          kicker={lang === "zh" ? "为什么选择我们" : "Why us"}
          title={whyUs.heading}
          image={whyUsImage}
          points={whyPoints}
        />
      )}

      {/* Remaining sections as PwC-style tabs */}
      {tabs.length > 0 && (
        <PossibilityTabs
          kicker={lang === "zh" ? "深入了解" : "Deep dive"}
          title={lang === "zh" ? "我们如何帮助您。" : "How we help you make it happen."}
          intro={lang === "zh"
            ? "从合规到优化——选择您感兴趣的话题，了解我们的方法。"
            : "From compliance to optimisation — pick a topic to explore how we work with you."}
          image={possibilityFallbackImage}
          tabs={tabs}
        />
      )}

      {/* Related case-study cards */}
      {related.length > 0 && (
        <>
          <ScrollReveal>
            <div className="max-w-[1480px] mx-auto px-6 lg:px-10 pt-20">
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.06] max-w-3xl">
                {lang === "zh" ? "客户案例与相关服务" : "Client stories & related services"}
              </h2>
            </div>
          </ScrollReveal>
          <CaseStudyCards ids={related} viewMoreId="services-pricing" />
        </>
      )}
    </div>
  );
}
